import React, { useState } from 'react';
import axios from 'axios';
import { FaCalendar, FaClipboardList, FaEdit } from 'react-icons/fa';
import Swal from 'sweetalert2';
import './LeaveRequest.css';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';

const LeaveRequest = () => {
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    leave_type: '',
    reason: '',
    expected_delivery_date: ''
  });
  const [displayDates, setDisplayDates] = useState({
    start_date: '',
    end_date: '',
    expected_delivery_date: ''
  });
  const [maternityDates, setMaternityDates] = useState({
    earliest: null,
    latest: null
  });
  const [loading, setLoading] = useState(false);
  const baseUrl = "https://api.burrayhr.com";

  const showAlert = (type, message) => {
    Swal.fire({
      icon: type,
      title: type === 'success' ? 'Success!' : 'Error!',
      text: message,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      background: type === 'success' ? '#d4edda' : '#f8d7da',
      color: type === 'success' ? '#155724' : '#721c24',
    });
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    return `${day}/${month}/${year}`;
  };

  const formatDateForServer = (date) => {
    if (!date) return '';
    const [day, month, year] = date.split('/');
    return `${year}-${month}-${day}`;
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return null;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const calculateMaternityLeaveDates = (deliveryDate) => {
    if (!deliveryDate) return null;
    
    const expectedDate = new Date(deliveryDate);
    
    // Calculate 45 days before
    const earliestDate = new Date(expectedDate);
    earliestDate.setDate(expectedDate.getDate() - 45);
    
    // Calculate 21 days before
    const latestDate = new Date(expectedDate);
    latestDate.setDate(expectedDate.getDate() - 21);
    
    return {
      earliest: earliestDate.toISOString().split('T')[0],
      latest: latestDate.toISOString().split('T')[0],
      deliveryDate: expectedDate.toISOString().split('T')[0]
    };
  };

  const calculateEndDate = (startDate) => {
    if (!startDate) return null;
    const start = new Date(startDate);
    const end = new Date(start);
    // Subtract 1 from 98 since we're counting the start date as day 1
    end.setDate(start.getDate() + 97);
    return end.toISOString().split('T')[0];
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    setDisplayDates(prev => ({
      ...prev,
      [name]: formatDateForDisplay(value)
    }));

    if (name === 'expected_delivery_date') {
      const dates = calculateMaternityLeaveDates(value);
      if (dates) {
        setMaternityDates(dates);
        // Reset start and end dates when delivery date changes
        setFormData(prev => ({
          ...prev,
          start_date: '',
          end_date: '',
          expected_delivery_date: value
        }));
        setDisplayDates(prev => ({
          ...prev,
          start_date: '',
          end_date: ''
        }));
      }
    }

    // For maternity leave, automatically calculate end date when start date changes
    if (formData.leave_type === 'maternity' && name === 'start_date') {
      const endDate = calculateEndDate(value);
      if (endDate) {
        setFormData(prev => ({
          ...prev,
          end_date: endDate
        }));
        setDisplayDates(prev => ({
          ...prev,
          end_date: formatDateForDisplay(endDate)
        }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'leave_type') {
      // Reset form when switching to/from maternity leave
      if (value === 'maternity' || formData.leave_type === 'maternity') {
        setFormData(prev => ({
          ...prev,
          start_date: '',
          end_date: '',
          expected_delivery_date: '',
          leave_type: value,
          reason: ''
        }));
        setDisplayDates({
          start_date: '',
          end_date: '',
          expected_delivery_date: ''
        });
      } else {
        setFormData(prev => ({
          ...prev,
          leave_type: value
        }));
      }
    } else if (name.includes('date')) {
      handleDateChange(e);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateMaternityDates = () => {
    if (formData.leave_type === 'maternity' && formData.expected_delivery_date) {
      const deliveryDate = new Date(formData.expected_delivery_date);
      const startDate = new Date(formData.start_date);
      const today = new Date();
      
      if (deliveryDate < today) {
        showAlert('error', 'Expected delivery date cannot be in the past');
        return false;
      }

      const earliestDate = new Date(maternityDates.earliest);
      const latestDate = new Date(maternityDates.latest);

      if (startDate < earliestDate || startDate > latestDate) {
        showAlert('error', 'Start date must be between 45 and 21 days before expected delivery date');
        return false;
      }

      // Verify end date is exactly 98 days from start date
      const calculatedEndDate = calculateEndDate(formData.start_date);
      if (formData.end_date !== calculatedEndDate) {
        showAlert('error', 'End date must be exactly 98 days from start date');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();


    if (formData.leave_type !== 'maternity') {
      // Validate date ranges for non-maternity leave
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        showAlert('error', 'Start date cannot be in the past');
        return;
      }

      if (endDate < startDate) {
        showAlert('error', 'End date must be after start date');
        return;
      }
    } else {
      if (!validateMaternityDates()) {
        return;
      }
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const formattedData = {
        leave_type: formData.leave_type,
        reason: formData.reason,
        start_date: formData.start_date,
        end_date: formData.end_date,
        status: 'pending',
        ...(formData.leave_type === 'maternity' && {
          expected_delivery_date: formData.expected_delivery_date
        })
      };

      console.log('Submitting leave request:', formattedData);

      await axios.post(`${baseUrl}/api/leaves/`, formattedData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      showAlert('success', 'Leave request submitted successfully!');
      
      setFormData({
        start_date: '',
        end_date: '',
        leave_type: '',
        reason: '',
        expected_delivery_date: ''
      });
      setDisplayDates({
        start_date: '',
        end_date: '',
        expected_delivery_date: ''
      });
    } catch (error) {
      console.log('Error submitting leave request:', error.response?.data || error);
      const errorMessage = error.response?.data?.non_field_errors?.[0] || error.response?.data?.error || 'Failed to submit leave request';
      showAlert('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <>
     <Topbar />
     <Sidebar />
     <div className="main-content">
     <div className="leave-request-container">
      <div className="leave-request-header">
        <FaClipboardList style={{color: 'navy'}} className="" />
        <h2>Leave Request Form</h2>
      </div>

      <form onSubmit={handleSubmit} className="leave-request-form">
        <div className="form-group">
          <label>
            <FaClipboardList className="field-icon" />
            Leave Type
          </label>
          <select
            name="leave_type"
            value={formData.leave_type}
            onChange={handleChange}
            required
          >
            <option value="">Select Leave Type</option>
            <option value="vacation">Vacation</option>
            <option value="special">Special</option>
            <option value="sick">Sick</option>
            <option value="maternity">Maternity</option>
          </select>
        </div>

        {formData.leave_type === 'maternity' && (
          <>
            <div className="form-group">
              <label>
                <FaCalendar className="field-icon" />
                Expected Date of Delivery
              </label>
              <div className="date-input-group">
                <input
                  type="date"
                  name="expected_delivery_date"
                  value={formData.expected_delivery_date}
                  onChange={handleDateChange}
                  required
                  min={today}
                  className="date-picker"
                />
                <span className="date-display">{displayDates.expected_delivery_date || 'dd/mm/yyyy'}</span>
              </div>
            </div>

            {formData.expected_delivery_date && (
              <>
                <div className="maternity-leave-info">
                  <div className="info-header">
                    <FaCalendar className="info-icon" />
                    <h3>Maternity Leave Guidelines</h3>
                  </div>
                  <div className="leave-period-details">
                    <p>Based on your expected delivery date:</p>
                    <div className="date-range">
                      <span className="date-label">Earliest start:</span>
                      <span className="date-value">{formatDateForDisplay(maternityDates.earliest)}</span>
                    </div>
                    <div className="date-range">
                      <span className="date-label">Latest start:</span>
                      <span className="date-value">{formatDateForDisplay(maternityDates.latest)}</span>
                    </div>
                    <p className="info-note">
                      Please select your start date between these dates. Your leave will automatically be set to 98 days from your start date.
                    </p>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <FaCalendar className="field-icon" />
                    Start Date
                  </label>
                  <div className="date-input-group">
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleDateChange}
                      required
                      min={maternityDates.earliest}
                      max={maternityDates.latest}
                      className="date-picker"
                    />
                    <span className="date-display">{displayDates.start_date || 'dd/mm/yyyy'}</span>
                  </div>
                </div>

                {formData.start_date && (
                  <div className="form-group">
                    <label>
                      <FaCalendar className="field-icon" />
                      End Date (98 days from start)
                    </label>
                    <div className="date-input-group">
                      <input
                        type="date"
                        name="end_date"
                        value={formData.end_date}
                        readOnly
                        className="date-picker"
                      />
                      <span className="date-display">{displayDates.end_date || 'dd/mm/yyyy'}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {formData.leave_type !== 'maternity' && (
          <>
            <div className="form-group">
              <label>
                <FaCalendar className="field-icon" />
                Start Date
              </label>
              <div className="date-input-group">
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  required
                  min={today}
                  className="date-picker"
                />
                <span className="date-display">{displayDates.start_date || 'dd/mm/yyyy'}</span>
              </div>
            </div>

            <div className="form-group">
              <label>
                <FaCalendar className="field-icon" />
                End Date
              </label>
              <div className="date-input-group">
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  required
                  min={formData.start_date || today}
                  className="date-picker"
                />
                <span className="date-display">{displayDates.end_date || 'dd/mm/yyyy'}</span>
              </div>
            </div>
          </>
        )}

        <div className="form-group">
          <label>
            <FaEdit className="field-icon" />
            Reason for Leave
          </label>
          <textarea
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            placeholder={formData.leave_type === 'maternity' ? 
              "Please provide additional details about your maternity leave request" : 
              "Please provide a reason for your leave request"}
            className="form-textarea"
            rows="4"
          />
        </div>

        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? 'Submitting...' : 'Submit Leave Request'}
        </button>
      </form>
    </div>
    </div>

    <style jsx>{`
      .maternity-leave-info {
        background-color: #f0f4ff;
        border: 1px solid #bfdbfe;
        border-radius: 8px;
        padding: 20px;
        margin: 20px 0;
      }

      .info-header {
        display: flex;
        align-items: center;
        margin-bottom: 15px;
        color: #1e3a8a;
      }

      .info-header h3 {
        margin: 0;
        margin-left: 10px;
        font-size: 1.1rem;
      }

      .info-icon {
        font-size: 1.2rem;
      }

      .leave-period-details {
        color: #1f2937;
      }

      .date-range {
        display: flex;
        align-items: center;
        margin: 10px 0;
        padding: 8px;
        background-color: #fff;
        border-radius: 4px;
        border: 1px solid #e5e7eb;
      }

      .date-label {
        font-weight: 600;
        width: 100px;
        color: #1e3a8a;
      }

      .date-value {
        margin-left: 10px;
        color: #000;
        font-family: monospace;
      }

      .info-note {
        margin-top: 15px;
        padding: 10px;
        background-color: #fffbeb;
        border-left: 4px solid #f59e0b;
        font-size: 0.9rem;
        color: #92400e;
      }
    `}</style>
    </>
  );
};

export default LeaveRequest;